import { useMemo, useState } from 'react'
import type { Competitor, BriefScope } from '@ispot/shared'
import { ColumnVisibilityToggle } from './ColumnVisibilityToggle'
import { CompetitorTableRow } from './CompetitorTableRow'

const COLUMN_LABELS: Record<string, string> = {
  name: 'Competitor',
  category: 'Category',
  type: 'Type',
  url: 'URL',
  pricing: 'Pricing',
  whatTheyDo: 'What They Do',
  strengths: 'Strengths',
  gaps: 'Gaps',
  notes: 'Notes',
  investors: 'Investors',
  hq: 'HQ',
  isAiFirst: 'AI-First',
}

const COLUMN_ORDER = [
  'type',
  'url',
  'pricing',
  'whatTheyDo',
  'strengths',
  'gaps',
  'notes',
  'investors',
  'hq',
  'isAiFirst',
] as const

interface CompetitorTableViewProps {
  competitors: Competitor[]
  visibleColumns: Record<string, boolean>
  onUpdateColumns: (cols: Record<string, boolean>) => void
  onUpdate: (id: string, data: Partial<Competitor>) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onOpenBrief: (scope: BriefScope) => void
  onAddClick: () => void
  onEnrich?: (id: string) => Promise<void>
}

export function CompetitorTableView({
  competitors,
  visibleColumns,
  onUpdateColumns,
  onUpdate,
  onDelete,
  onOpenBrief,
  onAddClick,
  onEnrich,
}: CompetitorTableViewProps) {
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState<'all' | Competitor['type']>('all')
  const [groupByCategory, setGroupByCategory] = useState(false)

  const visibleKeys = [
    'name',
    'category',
    ...COLUMN_ORDER.filter(k => visibleColumns[k]),
  ]

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Set(
        competitors
          .map(c => (c.category || '').trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b))
  }, [competitors])

  const filteredCompetitors = useMemo(() => {
    return competitors.filter(competitor => {
      const matchesCategory =
        categoryFilter === 'all'
          ? true
          : (competitor.category || '').trim() === categoryFilter
      const matchesType = typeFilter === 'all' ? true : competitor.type === typeFilter
      return matchesCategory && matchesType
    })
  }, [competitors, categoryFilter, typeFilter])

  const groupedCompetitors = useMemo(() => {
    const groups = new Map<string, Competitor[]>()
    for (const competitor of filteredCompetitors) {
      const category = (competitor.category || '').trim() || 'Uncategorized'
      const bucket = groups.get(category) ?? []
      bucket.push(competitor)
      groups.set(category, bucket)
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({
        category,
        items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }, [filteredCompetitors])

  async function handleConfirmSuggestion(id: string) {
    await onUpdate(id, { suggestedByAi: false })
  }

  const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
    background: 'transparent',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  }

  const totalColumns = visibleKeys.length + 2

  return (
    <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          gap: '16px',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <FilterChip
            ariaLabel="Category filter"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[
              { value: 'all', label: 'All categories' },
              ...categoryOptions.map(category => ({ value: category, label: category })),
            ]}
          />
          <FilterChip
            ariaLabel="Type filter"
            value={typeFilter}
            onChange={(value) => setTypeFilter(value as 'all' | Competitor['type'])}
            options={[
              { value: 'all', label: 'All types' },
              { value: 'direct', label: 'Direct' },
              { value: 'adjacent', label: 'Adjacent' },
              { value: 'potential', label: 'Potential' },
            ]}
          />
          <button
            type="button"
            onClick={() => setGroupByCategory(v => !v)}
            style={{
              padding: '7px 12px',
              background: groupByCategory ? 'var(--color-warm-200)' : 'transparent',
              border: groupByCategory ? '1px solid transparent' : '1px solid var(--color-border)',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 500,
              color: groupByCategory ? 'var(--color-text)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            Group by category
          </button>
          {(categoryFilter !== 'all' || typeFilter !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setCategoryFilter('all')
                setTypeFilter('all')
              }}
              style={{
                padding: '7px 10px',
                background: 'transparent',
                border: 'none',
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              letterSpacing: '0.05em',
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
            }}
          >
            {filteredCompetitors.length} shown
          </span>
          <ColumnVisibilityToggle visibleColumns={visibleColumns} onChange={onUpdateColumns} />
        </div>
      </div>

      {competitors.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px', color: 'var(--color-text-muted)' }}>
          <p style={{ fontSize: '14px' }}>No competitors yet. Add your first one.</p>
          <button onClick={onAddClick} style={{ padding: '8px 18px', background: 'var(--color-accent)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            + Add Competitor
          </button>
        </div>
      ) : filteredCompetitors.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '260px', gap: '10px', color: 'var(--color-text-muted)' }}>
          <p style={{ fontSize: '14px', margin: 0 }}>No competitors match this filter.</p>
          <button
            type="button"
            onClick={() => {
              setCategoryFilter('all')
              setTypeFilter('all')
            }}
            style={{ padding: '7px 14px', background: 'white', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', color: 'var(--color-text-secondary)' }}
          >
            Reset filters
          </button>
        </div>
      ) : (
        <div style={{ overflow: 'auto', flex: 1, padding: '0 24px 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr>
                {visibleKeys.map(key => (
                  <th key={key} style={thStyle}>{COLUMN_LABELS[key] ?? key}</th>
                ))}
                <th style={thStyle}>Brief</th>
                <th style={{ ...thStyle, width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              {groupByCategory
                ? groupedCompetitors.map(group => (
                    <GroupSection
                      key={group.category}
                      category={group.category}
                      competitors={group.items}
                      totalColumns={totalColumns}
                      visibleColumns={visibleColumns}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onConfirmSuggestion={handleConfirmSuggestion}
                      onOpenBrief={onOpenBrief}
                      onEnrich={onEnrich}
                    />
                  ))
                : filteredCompetitors.map(competitor => (
                    <CompetitorTableRow
                      key={competitor.id}
                      competitor={competitor}
                      visibleColumns={visibleColumns}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onConfirmSuggestion={handleConfirmSuggestion}
                      onOpenBrief={onOpenBrief}
                      onEnrich={onEnrich ? () => onEnrich(competitor.id) : undefined}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FilterChip({
  ariaLabel,
  value,
  onChange,
  options,
}: {
  ariaLabel: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '7px 28px 7px 12px',
        background: 'var(--color-warm-100)',
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23676763' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        backgroundSize: '10px 10px',
        border: '1px solid transparent',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--color-text)',
        appearance: 'none',
        cursor: 'pointer',
      }}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

function GroupSection({
  category,
  competitors,
  totalColumns,
  visibleColumns,
  onUpdate,
  onDelete,
  onConfirmSuggestion,
  onOpenBrief,
  onEnrich,
}: {
  category: string
  competitors: Competitor[]
  totalColumns: number
  visibleColumns: Record<string, boolean>
  onUpdate: (id: string, data: Partial<Competitor>) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onConfirmSuggestion: (id: string) => Promise<void>
  onOpenBrief: (scope: BriefScope) => void
  onEnrich?: (id: string) => Promise<void>
}) {
  return (
    <>
      <tr data-testid={`category-group-${category}`}>
        <td
          colSpan={totalColumns}
          style={{
            padding: '22px 12px 10px',
            borderTop: 'none',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '17px',
                fontWeight: 500,
                letterSpacing: '-0.012em',
                color: 'var(--color-text)',
              }}
            >
              {category}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--color-text-muted)',
              }}
            >
              {competitors.length} in field
            </span>
          </div>
        </td>
      </tr>
      {competitors.map(competitor => (
        <CompetitorTableRow
          key={competitor.id}
          competitor={competitor}
          visibleColumns={visibleColumns}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onConfirmSuggestion={onConfirmSuggestion}
          onOpenBrief={onOpenBrief}
          onEnrich={onEnrich ? () => onEnrich(competitor.id) : undefined}
        />
      ))}
    </>
  )
}
